import {
	UseQueryResult,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";

import { SATOSPortal } from "@/DataAdapter/SATOSPortal";

import {
	SortDirection,
	TaskGroup,
	TaskRequest,
} from "@/model/Task/TaskRequest";

export const useCreateTask = () => {
	const queryClient = useQueryClient();
	const { mutateAsync: createTask, isPending: isSubmitting } = useMutation({
		mutationFn: (request: TaskRequest) => SATOSPortal.CreateTask(request),
		onMutate: (newTask) => {
			queryClient.setQueryData([TaskQueryKeys.tasks], newTask);
		},
		onError: (_, __, previousTask) => {
			queryClient.setQueryData([TaskQueryKeys.tasks], previousTask);
		},
		onSettled: () => {
			queryClient.invalidateQueries({
				queryKey: [TaskQueryKeys.tasks],
			});
		},
	});
	return { createTask, isSubmitting };
};

export const useCreateTaskComment = () => {
	const queryClient = useQueryClient();

	const { mutateAsync: createTaskComment, isPending: isSubmitting } =
		useMutation({
			mutationFn: (request: any) => SATOSPortal.CreateTaskComment(request),

			onMutate: async (newComment) => {
				// Optionally cancel any outgoing refetches
				await queryClient.cancelQueries({ queryKey: [TaskQueryKeys.tasks] });

				// Optionally return rollback snapshot (for optimistic updates)
				const previousData = queryClient.getQueryData([TaskQueryKeys.tasks]);
				return previousData;
			},

			onError: (_, __, context) => {
				if (context) {
					queryClient.setQueryData([TaskQueryKeys.tasks], context);
				}
			},

			onSettled: () => {
				queryClient.invalidateQueries({ queryKey: [TaskQueryKeys.tasks] });
			},
		});

	return { createTaskComment, isSubmitting };
};

export const useUpdateTask = () => {
	const queryClient = useQueryClient();

	const { mutateAsync: updateTask, isPending: isSubmitting } = useMutation({
		mutationFn: ({
			taskId,
			request,
		}: {
			taskId: number;
			request: TaskRequest;
		}) => SATOSPortal.UpdateTask(taskId, request), // adjust according to actual API
		onMutate: async (updatedData) => {
			await queryClient.cancelQueries({ queryKey: [TaskQueryKeys.tasks] });

			const previousTasks = queryClient.getQueryData([TaskQueryKeys.tasks]);

			queryClient.setQueryData([TaskQueryKeys.tasks], (old: any) => {
				if (!old?.Items) return old;
				return {
					...old,
					Items: old.Items.map((task: any) =>
						task.TaskId === updatedData.taskId
							? { ...task, ...updatedData.request }
							: task,
					),
				};
			});

			return previousTasks;
		},
		onError: (_, __, context) => {
			if (context) {
				queryClient.setQueryData([TaskQueryKeys.tasks], context);
			}
		},
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey: [TaskQueryKeys.tasks] });
		},
	});

	return { updateTask, isSubmitting };
};

export const useFetchTaskGroupsByStatus = (statuses: string[]) => {
	return useQuery<any>({
		queryKey: [TaskQueryKeys.tasks, statuses],
		queryFn: () => SATOSPortal.GetTaskGroupsByStatus(statuses),
		enabled: statuses.length > 0, // Only run if there are statuses
	});
};

export const useFetchTaskGroups = (): UseQueryResult<TaskGroup[]> => {
	return useQuery({
		queryKey: ["taskGroups"],
		queryFn: SATOSPortal.GetTaskGroupsAPI,
		staleTime: 5 * 60 * 1000, // Optional: cache for 5 minutes
		// enabled: true, // Optional: can be dynamic if needed
	});
};

export const useFetchTaskById = (taskId: number) => {
	return useQuery<any, any>({
		queryKey: ["taskGroups", taskId],
		queryFn: () => SATOSPortal.GetTaskSingle(taskId),
		// enabled: isDialogueOpen
	});
};

export const useFetchTaskByIdRecurring = (taskId: number) => {
	return useQuery<any, any>({
		queryKey: ["tasks, task", taskId],
		queryFn: () => SATOSPortal.GetTaskSingleRecurring(taskId!, true),
		enabled: !!taskId, // ensure it's only called when taskId is available
	});
};

export const useFetchTasks = (
	groupIds: number[],
	status: string,
	page: number,
	pageSize: number,
	searchTermTable?: string,
	sort?: SortDirection,
) => {
	return useQuery({
		queryKey: [
			"tasks",
			{ groupIds, status, page, pageSize, searchTermTable, sort },
		],
		queryFn: () =>
			SATOSPortal.GetTasks(
				groupIds,
				status,
				page,
				pageSize,
				searchTermTable,
				sort,
			),
	});
};

export const useFetchProcedureUpdateSuggestions = (
	hasMaterialHandling: boolean,
) => {
	return useQuery<any, any>({
		queryKey: [TaskQueryKeys.procedureUpdatesSuggestions],
		queryFn: () => SATOSPortal.GetProcedureUpdate(hasMaterialHandling),
	});
};

export class TaskQueryKeys {
	static readonly tasks = "tasks";
	static readonly comments = "comments";
	static readonly groups = "groups";
	static readonly userdata = "UserData";
	static readonly tagnrlist = "tagnrlist";
	static readonly pagination = "pagination";
	static readonly procedureUpdatesSuggestions = "procedureUpdatesSuggestions";
	static readonly newImprov = "newImprov";
	static readonly myImprov = "myImprov";
	static readonly verified = "verified";
	static readonly declined = "declined";
}
